import React, {useEffect, useState} from 'react';
import Button, {ButtonType} from "../../../../../components/ui/Button/Button";
import {useTranslation} from "react-i18next";
import Alert from "../../../../../components/ui/Alert/Alert";
import {IError} from "../../../../../app/interfaces/response/IResponse";
import {ResponseResultCode} from "../../../../../app/enums/ResponseResultCode";
import {toast} from "react-toastify";
import {isAxiosError} from "axios";
import {BLErrorCode} from "../../../../../app/enums/BLErrorCode";
import { IDeleteCustomerAttachmentPostAction } from '../../../../../app/interfaces/customerAttachment/IDeleteCustomerAttachmentPostAction';
import useCustomerAttachmentApi from '../../../../../app/api/customerAttachment';

type DeleteCustomerAttachmentProps = {
    docToDelete: IDeleteCustomerAttachmentPostAction | null;
    close?: () => void;
}

const DeleteCustomerAttachment: React.FC<DeleteCustomerAttachmentProps> = ({
                                                           docToDelete,
                                                           close
                                                       }) => {
    const {t} = useTranslation();
    const [disabled, setDisabled] = useState<boolean>(false);
    const [errors, setErrors] = useState<Array<IError>>([]);

    const {
        deleteCustomerAttachmentDoc: {
            query: deleteCustomerAttachmentDoc,
            cancel
        }
    } = useCustomerAttachmentApi();

    useEffect(() => {
        return () => {
            cancel();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async (): Promise<void> => {
        setDisabled(prev => !prev);
        setErrors([]);
        try {
            if (!docToDelete) {
                return;
            }
            const response = await deleteCustomerAttachmentDoc(docToDelete);

            if (response?.status === 200 && response.data.resultCode === ResponseResultCode.Ok) {
                if (close) {
                    close();
                }

                document.dispatchEvent(new CustomEvent('onGridMessage', {
                    detail: {
                        action: 'updateRows',
                        rows: [
                            {
                                id: docToDelete.orderReleaseGid,
                                isRowLoading: true
                            }
                        ]
                    }
                }));

                // toast.success(<ToastLayout text={t("shared.labels.delayed-update-warning")}
                //                            title={`${t("shared.labels.shipment")} ${sId}`}/>);
            }
        } catch (err) {
            if (isAxiosError(err)) {
                if (err.response && err.response.data && err.response.status === 422 &&
                    err.response.data.resultCode === ResponseResultCode.NotValidData &&
                    err.response.data.errors && Array.isArray(err.response.data.errors)) {
                    setErrors(err.response.data.errors);
                } else if (close) {
                    close();
                    toast.error(err.message);
                }
            }
        }

        setDisabled(prev => !prev);
    }

    return (
        <div className={'w100'}>
            <div className={'dialog-default-content-container'}>
                {errors.length > 0 && errors.map((err, index) => {
                    switch (err.errorCode) {
                        case BLErrorCode.NotFoundCustomerAttachmentDoc:
                            return (
                                <Alert type={'Error'}
                                       id={`delete-customer-attachment-error-${index}`}>
                                    {t("customer-attachment.errors.delete-attachment-not-found")}
                                </Alert>
                            )
                    }

                    return null;
                })}
                {errors.length <= 0 &&
                    <div style={{margin:'15px', fontSize: '14px', textAlign: 'center'}}>
                        {t("customer-attachment.labels.deleting-doc-confirmation-text")}
                    </div>
                }
            </div>
            <div className={'dialog-default-buttons-container'} style={{marginBottom: '15px'}}>
                {close &&
                    <Button buttonType={ButtonType.Transparent}
                            disabled={disabled}
                            style={{minWidth: '150px'}}
                            className={'close-button'}
                            onClick={close}>
                        {t("shared.btn.no")}
                    </Button>
                }
                <Button buttonType={ButtonType.Primary}
                        disabled={disabled}
                        style={{minWidth: '150px'}}
                        onClick={submit}
                        className={'confirm-button'}
                        isLoading={disabled}>
                    {t("shared.btn.yes")}
                </Button>
            </div>
        </div>
    );
};

export default DeleteCustomerAttachment;
